import { defineStore } from 'pinia';
import { computed, ref, watch } from 'vue';
import { api, client } from '@/api/client';
import { useAuthStore } from './auth.store';
import { applyTheme, availableThemes } from '@/composables/useTheme';
import { applyCompanyBranding } from '@/composables/useCompanyBranding';
import type { ArchivedCompany, ArchivedProject, Board, BoardBadgeCount, Company, IncidentSummary, PluginNavPage, Project, ProjectMigrationReport, Role, ThemeKey } from '@/types';
import type { VotingSummary } from '@/types/voting';

const COMPANY_KEY = 'pro-kanban.company';
/** Cards move in bursts; one refresh after the burst is enough for a counter. */
const BADGE_REFRESH_DEBOUNCE_MS = 1500;

const NO_VOTES: VotingSummary = { enabled: false, awaitingMe: 0, open: 0 };

const NO_INCIDENTS: IncidentSummary = {
  enabled: false,
  total: 0,
  open: 0,
  unacknowledged: 0,
  critical: 0,
  mine: 0,
  recurring: 0,
  overdue: 0,
  badges: [],
};

/**
 * Holds the currently selected company and project plus their child lists.
 *
 * Selecting a company loads the caller's effective permissions, applies the
 * company theme and joins the matching realtime room.
 */
export const useWorkspaceStore = defineStore('workspace', () => {
  const auth = useAuthStore();

  const companies = ref<Company[]>([]);
  const company = ref<Company | null>(null);
  const roles = ref<Role[]>([]);
  const projects = ref<Project[]>([]);
  const project = ref<Project | null>(null);
  const boards = ref<Board[]>([]);
  /** Board lists per project, so the sidebar can expand a project it is not in. */
  const boardsByProject = ref<Record<string, Board[]>>({});
  const loading = ref(false);
  /** Keys of the plugins enabled for the current project — drives their nav entries. */
  const enabledPlugins = ref<Set<string>>(new Set());
  /**
   * Keys enabled at *company* level. Kept apart from `enabledPlugins` rather
   * than merged: a plugin switched on for one project does not make the
   * company-level feature work, because the company-scoped gate reads the
   * company installation. Merging them would put a navigation entry in front of
   * a screen that answers `plugin_disabled`.
   */
  const enabledCompanyPlugins = ref<Set<string>>(new Set());
  /** Pages those plugins contribute, so the sidebar can link to them directly. */
  const pluginPages = ref<PluginNavPage[]>([]);
  /** Resolved board badges for the whole company — the sidebar counters. */
  const badgeCounts = ref<BoardBadgeCount[]>([]);
  /**
   * Incident counts for the company. Company-scoped rather than per-project,
   * because an incident has no project until somebody gives it one, so the
   * sidebar entry has to exist before any project is open.
   */
  const incidents = ref<IncidentSummary>(NO_INCIDENTS);
  /**
   * Vote counts for the company, and the same trick the incident summary
   * plays: the endpoint answers `enabled: false` rather than failing when the
   * plugin is off, so one call both fills the badge and decides whether the
   * navigation entry exists at all.
   */
  const votes = ref<VotingSummary>(NO_VOTES);

  const companyId = computed(() => company.value?.id ?? null);
  const projectId = computed(() => project.value?.id ?? null);

  /**
   * Keeps branding in step with the company object.
   *
   * Selecting a company applies it directly; this covers the other path, where
   * the settings screen saves and swaps in an updated company.
   */
  watch(company, (value) => applyCompanyBranding(value), { deep: true });

  /** Badges of one board, in the order they were configured. */
  const badgesByBoard = computed(() => {
    const map = new Map<string, BoardBadgeCount[]>();
    for (const badge of badgeCounts.value) {
      const list = map.get(badge.boardId);
      if (list) list.push(badge);
      else map.set(badge.boardId, [badge]);
    }
    return map;
  });

  /** Every badge of every board in a project, so a collapsed project still counts. */
  const badgesByProject = computed(() => {
    const map = new Map<string, BoardBadgeCount[]>();
    for (const badge of badgeCounts.value) {
      const list = map.get(badge.projectId);
      if (list) list.push(badge);
      else map.set(badge.projectId, [badge]);
    }
    return map;
  });

  async function loadCompanies(): Promise<Company[]> {
    companies.value = await api.get<Company[]>('companies');
    return companies.value;
  }

  async function selectCompany(id: string): Promise<Company | null> {
    if (company.value?.id === id) return company.value;

    loading.value = true;
    try {
      const selected = await api.get<Company>(`companies/${id}`);
      if (company.value) client.realtime.unsubscribe([`company:${company.value.id}`]);
      clearProject();
      company.value = selected;
      projects.value = [];
      roles.value = [];
      localStorage.setItem(COMPANY_KEY, id);

      await auth.loadPermissions(id);
      client.realtime.subscribe([`company:${id}`]);

      // Branding first: it decides which themes the next line may even pick from.
      applyCompanyBranding(company.value);
      // The member's own theme wins unless the company governs the list and bars it.
      const preferred = (auth.user?.theme as ThemeKey) ?? company.value.theme;
      applyTheme(availableThemes.value.includes(preferred) ? preferred : company.value.theme);

      const [projectList, roleList] = await Promise.all([
        api.get<Project[]>(`companies/${id}/projects`),
        auth.can('roles.view') ? api.get<Role[]>(`companies/${id}/roles`) : Promise.resolve([]),
      ]);
      projects.value = projectList;
      roles.value = roleList;
      badgeCounts.value = [];
      incidents.value = NO_INCIDENTS;
      votes.value = NO_VOTES;
      enabledCompanyPlugins.value = new Set();
      boardsByProject.value = {};
      // Not awaited: the sidebar renders without counters and fills them in.
      void refreshBadges();
      void refreshIncidents();
      void refreshVotes();
      void refreshCompanyPlugins();
      return company.value;
    } finally {
      loading.value = false;
    }
  }

  /** Picks the last used company, or the first one the user belongs to. */
  async function ensureCompany(): Promise<Company | null> {
    if (company.value) return company.value;
    if (!companies.value.length) await loadCompanies();
    if (!companies.value.length) return null;

    const saved = localStorage.getItem(COMPANY_KEY);
    const target = companies.value.find((entry) => entry.id === saved) ?? companies.value[0];
    return selectCompany(target.id);
  }

  async function selectProject(id: string): Promise<Project | null> {
    if (project.value?.id === id) return project.value;

    const selected = await api.get<Project>(`projects/${id}`);
    // Opening a project from a deep link may cross into another company.
    const owningCompanyId = selected.company?.id;
    if (owningCompanyId && company.value?.id !== owningCompanyId) {
      await selectCompany(owningCompanyId);
    }

    clearProject();
    project.value = selected;
    client.realtime.subscribe([`project:${id}`]);
    boards.value = await api.get<Board[]>(`projects/${id}/boards`);
    boardsByProject.value = { ...boardsByProject.value, [id]: boards.value };
    await refreshPlugins();

    // Project theme override, if the project defines one.
    if (selected.theme) applyTheme(selected.theme);
    return project.value;
  }

  /** Plugins can add whole sections, so the navigation needs their state. */
  async function refreshPlugins(): Promise<void> {
    if (!projectId.value || !auth.can('projects.view')) {
      enabledPlugins.value = new Set();
      pluginPages.value = [];
      return;
    }
    try {
      const response = await api.get<{ keys: string[]; pages: PluginNavPage[] }>(`projects/${projectId.value}/plugins/enabled`);
      enabledPlugins.value = new Set(response.keys);
      pluginPages.value = response.pages ?? [];
    } catch {
      enabledPlugins.value = new Set();
      pluginPages.value = [];
    }
  }

  const pluginEnabled = (key: string): boolean => enabledPlugins.value.has(key);

  /** Company-level plugin state, for the navigation entries that depend on it. */
  async function refreshCompanyPlugins(): Promise<void> {
    if (!companyId.value) {
      enabledCompanyPlugins.value = new Set();
      return;
    }
    try {
      const response = await api.get<{ keys: string[] }>(`companies/${companyId.value}/plugins/enabled`);
      enabledCompanyPlugins.value = new Set(response.keys);
    } catch {
      enabledCompanyPlugins.value = new Set();
    }
  }

  const companyPluginEnabled = (key: string): boolean => enabledCompanyPlugins.value.has(key);

  function clearProject(): void {
    if (project.value) client.realtime.unsubscribe([`project:${project.value.id}`]);
    project.value = null;
    boards.value = [];
    enabledPlugins.value = new Set();
    pluginPages.value = [];
  }

  async function createCompany(payload: { name: string; slug?: string; timezone?: string }): Promise<Company> {
    const created = await api.post<Company>('companies', payload);
    companies.value = [...companies.value, created];
    await selectCompany(created.id);
    return created;
  }

  async function createProject(payload: {
    name: string;
    key?: string;
    description?: string;
    icon?: string;
    logoFileId?: string;
    color?: string;
    visibility?: string;
    workflowTemplate?: string;
  }): Promise<Project> {
    if (!companyId.value) throw new Error('No company selected');
    const created = await api.post<Project>(`companies/${companyId.value}/projects`, payload);
    projects.value = [...projects.value, created];
    return created;
  }

  /**
   * Archives the current company and leaves it.
   *
   * Leaving is not a courtesy: the company is gone from `GET /companies` the
   * moment it is archived, so every screen still mounted is pointed at a
   * workspace the API will not serve. The caller lands wherever `ensureCompany`
   * takes them — another company, or the create screen when this was the only
   * one — and gets the company back from the personal cabinet.
   */
  async function archiveCompany(id: string): Promise<void> {
    await api.post(`companies/${id}/archive`);
    if (company.value?.id === id) {
      client.realtime.unsubscribe([`company:${id}`]);
      clearProject();
      company.value = null;
      projects.value = [];
      roles.value = [];
      localStorage.removeItem(COMPANY_KEY);
    }
    await loadCompanies();
  }

  /** Brings one back and selects it, which is what the person asked for. */
  async function restoreCompany(id: string): Promise<Company> {
    const restored = await api.post<Company>(`companies/${id}/restore`);
    await loadCompanies();
    await selectCompany(id);
    return restored;
  }

  /**
   * Destroys an archived company for good.
   *
   * Offered only from the restore list, and only to the owner: the point of the
   * cabinet entry is that somebody looking at what they put away is the person
   * best placed to decide it is not coming back.
   */
  async function deleteCompany(id: string): Promise<void> {
    await api.delete(`companies/${id}`);
    await loadCompanies();
  }

  async function loadArchivedCompanies(): Promise<ArchivedCompany[]> {
    return api.get<ArchivedCompany[]>('companies/archived');
  }

  async function loadArchivedProjects(): Promise<ArchivedProject[]> {
    return api.get<ArchivedProject[]>('projects/archived');
  }

  /**
   * Restores an archived project.
   *
   * The project list is only refreshed when the restored project belongs to the
   * company currently open — from the cabinet it usually does not, and reloading
   * somebody else's list would be a wasted round trip.
   */
  async function restoreProject(id: string, owningCompanyId: string): Promise<void> {
    await api.post(`projects/${id}/restore`);
    if (companyId.value === owningCompanyId) await refreshProjects();
  }

  async function deleteProject(id: string, owningCompanyId: string): Promise<void> {
    await api.delete(`projects/${id}`);
    if (project.value?.id === id) clearProject();
    if (companyId.value === owningCompanyId) await refreshProjects();
  }

  /** What moving this project into `targetCompanyId` would do. Writes nothing. */
  async function previewProjectMove(id: string, targetCompanyId: string): Promise<ProjectMigrationReport> {
    return api.post<ProjectMigrationReport>(`projects/${id}/move/preview`, { targetCompanyId, confirmed: false });
  }

  /**
   * Moves the project, then re-reads both company's project lists through the
   * one that is open: the project has left the current company or arrived in
   * it, and either way the sidebar is now describing something that is not
   * there.
   */
  async function moveProject(id: string, targetCompanyId: string): Promise<ProjectMigrationReport> {
    const report = await api.post<ProjectMigrationReport>(`projects/${id}/move`, { targetCompanyId, confirmed: true });
    if (project.value?.id === id) clearProject();
    await refreshProjects();
    return report;
  }

  async function refreshProjects(): Promise<void> {
    if (!companyId.value) return;
    projects.value = await api.get<Project[]>(`companies/${companyId.value}/projects`);
  }

  async function refreshBoards(): Promise<void> {
    if (!projectId.value) return;
    boards.value = await api.get<Board[]>(`projects/${projectId.value}/boards`);
    boardsByProject.value = { ...boardsByProject.value, [projectId.value]: boards.value };
  }

  /** Boards of any project, fetched once and then served from the cache. */
  async function loadProjectBoards(id: string): Promise<Board[]> {
    const cached = boardsByProject.value[id];
    if (cached) return cached;
    const list = await api.get<Board[]>(`projects/${id}/boards`);
    boardsByProject.value = { ...boardsByProject.value, [id]: list };
    return list;
  }

  const defaultBoard = computed(() => boards.value.find((board) => board.isDefault) ?? boards.value[0] ?? null);

  /**
   * Recounts every badge in the company. Boards without badges cost nothing on
   * the server, so this stays cheap even with a long project list.
   */
  async function refreshBadges(): Promise<void> {
    if (!companyId.value || !auth.can('boards.view')) {
      badgeCounts.value = [];
      return;
    }
    try {
      badgeCounts.value = await api.get<BoardBadgeCount[]>(`companies/${companyId.value}/board-badges`);
    } catch {
      // A stale counter is better than an error toast on every card move.
    }
  }

  /**
   * Incident counts for the sidebar.
   *
   * The endpoint answers `enabled: false` rather than failing when the plugin
   * is off, so this is also how the SPA learns whether the section exists at
   * all — one call instead of a separate capability probe.
   */
  async function refreshIncidents(): Promise<void> {
    if (!companyId.value || !auth.can('incidents.view')) {
      incidents.value = NO_INCIDENTS;
      return;
    }
    try {
      incidents.value = await api.get<IncidentSummary>(`companies/${companyId.value}/incidents/summary`);
    } catch {
      // Same reasoning as the board badges: a stale counter beats a toast.
    }
  }

  const incidentsEnabled = computed(() => incidents.value.enabled);

  /** Vote counts for the sidebar, gated the same way the incident summary is. */
  async function refreshVotes(): Promise<void> {
    if (!companyId.value || !auth.can('voting.view')) {
      votes.value = NO_VOTES;
      return;
    }
    try {
      votes.value = await api.get<VotingSummary>(`companies/${companyId.value}/votes/summary`);
    } catch {
      // Same reasoning as the board badges: a stale counter beats a toast.
    }
  }

  const votingEnabled = computed(() => votes.value.enabled);

  let badgeTimer: ReturnType<typeof setTimeout> | null = null;
  function scheduleBadgeRefresh(): void {
    if (badgeTimer) clearTimeout(badgeTimer);
    badgeTimer = setTimeout(() => {
      badgeTimer = null;
      void refreshBadges();
    }, BADGE_REFRESH_DEBOUNCE_MS);
  }

  let incidentTimer: ReturnType<typeof setTimeout> | null = null;
  function scheduleIncidentRefresh(): void {
    if (incidentTimer) clearTimeout(incidentTimer);
    incidentTimer = setTimeout(() => {
      incidentTimer = null;
      void refreshIncidents();
    }, BADGE_REFRESH_DEBOUNCE_MS);
  }

  let voteTimer: ReturnType<typeof setTimeout> | null = null;
  function scheduleVoteRefresh(): void {
    if (voteTimer) clearTimeout(voteTimer);
    voteTimer = setTimeout(() => {
      voteTimer = null;
      void refreshVotes();
    }, BADGE_REFRESH_DEBOUNCE_MS);
  }

  let stopBadgeSync: (() => void) | null = null;
  /** Keeps the sidebar counters in step with activity anywhere in the company. */
  function startBadgeSync(): void {
    if (stopBadgeSync) return;
    stopBadgeSync = client.realtime.on<{ event: string }>('event', (message) => {
      if (message.event?.startsWith('Card')) scheduleBadgeRefresh();
      if (message.event?.startsWith('Incident')) scheduleIncidentRefresh();
      if (message.event?.startsWith('Vote')) scheduleVoteRefresh();
    });
  }

  function projectName(entry: Project): string {
    const locale = (auth.user?.locale ?? 'en') as string;
    return entry.nameTranslations?.[locale] ?? entry.name;
  }

  return {
    companies,
    company,
    companyId,
    roles,
    projects,
    project,
    projectId,
    boards,
    boardsByProject,
    loadProjectBoards,
    defaultBoard,
    loading,
    enabledPlugins,
    enabledCompanyPlugins,
    companyPluginEnabled,
    refreshCompanyPlugins,
    pluginPages,
    badgeCounts,
    badgesByBoard,
    badgesByProject,
    incidents,
    incidentsEnabled,
    refreshIncidents,
    votes,
    votingEnabled,
    refreshVotes,
    refreshBadges,
    startBadgeSync,
    pluginEnabled,
    refreshPlugins,
    loadCompanies,
    selectCompany,
    ensureCompany,
    selectProject,
    clearProject,
    createCompany,
    archiveCompany,
    restoreCompany,
    deleteCompany,
    loadArchivedCompanies,
    loadArchivedProjects,
    restoreProject,
    deleteProject,
    previewProjectMove,
    moveProject,
    createProject,
    refreshProjects,
    refreshBoards,
    projectName,
  };
});
